import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, RefreshCw } from 'lucide-react';
import { keysApi } from '@/api';
import { useToast } from '@/hooks';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { CopyId, PageHeader, StatStrip, StatusPill } from '@/components/page';
import {
  API_URL,
  Callout,
  CodeBlock,
  ConfirmDialog,
  DevLabel,
  EmptyState,
  Mono,
  SkeletonRows,
  Tag,
  fmtDate,
} from '@/components/oauth';
import { cn } from '@/lib/utils';
import type { SigningKey } from '@/types';

const JWKS_URL = `${API_URL}/.well-known/jwks.json`;
const DAY = 864e5;

type KeyState = 'current' | 'rotating' | 'retired';

const daysUntil = (v: string | null) => (v ? Math.ceil((new Date(v).getTime() - Date.now()) / DAY) : null);

function stateOf(k: SigningKey): KeyState {
  const d = daysUntil(k.expires_at);
  if (!k.is_active || (d !== null && d <= 0)) return 'retired';
  return k.is_current ? 'current' : 'rotating';
}

const STATE_PILL: Record<KeyState, React.ReactNode> = {
  current: <StatusPill tone="ok">Current</StatusPill>,
  rotating: <StatusPill tone="warn">Rotating out</StatusPill>,
  retired: <StatusPill>Retired</StatusPill>,
};

/** Trim long base64url moduli so the published JWKS stays readable. */
function shortenJwks(raw: unknown): unknown {
  const jwks = raw as { keys?: Record<string, unknown>[] };
  if (!jwks || !Array.isArray(jwks.keys)) return raw;
  return {
    ...jwks,
    keys: jwks.keys.map((k) => ({
      ...k,
      n: typeof k.n === 'string' && k.n.length > 24 ? `${k.n.slice(0, 22)}…` : k.n,
    })),
  };
}

export function KeysPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [retireTarget, setRetireTarget] = useState<SigningKey | null>(null);

  const {
    data: keys = [],
    isLoading,
    isError,
    refetch,
  } = useQuery({ queryKey: ['signing-keys'], queryFn: keysApi.list });

  const { data: jwks } = useQuery({
    queryKey: ['jwks-public'],
    queryFn: async () => {
      const res = await fetch(JWKS_URL);
      if (!res.ok) throw new Error('JWKS unavailable');
      return (await res.json()) as { keys?: unknown[] };
    },
    retry: false,
  });

  const rotateKey = useMutation({
    mutationFn: keysApi.rotate,
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['signing-keys'] });
      void queryClient.invalidateQueries({ queryKey: ['jwks-public'] });
      setConfirmRotate(false);
      toast({ title: 'Signing key rotated', description: `New kid: ${result.kid}` });
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Rotation failed',
        description: error instanceof Error ? error.message : 'Failed to rotate signing key',
      });
    },
  });

  const retireKey = useMutation({
    mutationFn: (kid: string) => keysApi.retire(kid),
    onSuccess: (_, kid) => {
      void queryClient.invalidateQueries({ queryKey: ['signing-keys'] });
      void queryClient.invalidateQueries({ queryKey: ['jwks-public'] });
      setRetireTarget(null);
      toast({ title: 'Key retired', description: kid });
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Could not retire key',
        description: error instanceof Error ? error.message : 'Request failed',
      });
    },
  });

  const current = keys.find((k) => k.is_current && k.is_active) ?? keys.find((k) => stateOf(k) === 'current');
  const published = keys.filter((k) => stateOf(k) !== 'retired');
  const left = current ? daysUntil(current.expires_at) : null;
  const ageDays = current ? Math.max(0, Math.floor((Date.now() - new Date(current.created_at).getTime()) / DAY)) : 0;

  const jwksText = useMemo(
    () => (jwks ? JSON.stringify(shortenJwks(jwks), null, 2) : ''),
    [jwks]
  );
  const publishedCount = jwks?.keys?.length ?? published.length;

  return (
    <div>
      <PageHeader
        title="Signing keys"
        description="Keys that sign every ID and access token. Rotating publishes a new key and keeps the old one verifiable during the overlap window (7 days by default)."
        actions={
          <Button onClick={() => setConfirmRotate(true)} disabled={isLoading || rotateKey.isPending}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Rotate now
          </Button>
        }
      />

      <div className="space-y-5">
        {left !== null && left <= 14 && (
          <Callout tone="warn" title={`Current key expires in ${Math.max(left, 0)} day${left === 1 ? '' : 's'}`}>
            Rotate before it expires so tokens keep verifying without a gap.
          </Callout>
        )}

        {isLoading ? (
          <div className="rounded-lg border border-border bg-card">
            <SkeletonRows rows={4} />
          </div>
        ) : isError ? (
          <div className="rounded-lg border border-border bg-card">
            <EmptyState
              icon={<KeyRound />}
              title="Couldn’t load signing keys"
              action={
                <Button variant="outline" onClick={() => void refetch()}>
                  Try again
                </Button>
              }
            >
              Check your connection and try again.
            </EmptyState>
          </div>
        ) : keys.length === 0 ? (
          <div className="rounded-lg border border-border bg-card">
            <EmptyState icon={<KeyRound />} title="No signing keys found">
              A key is generated the first time a token is issued. You can also rotate to create one now.
            </EmptyState>
          </div>
        ) : (
          <>
            {current && (
              <section className="min-w-0 overflow-hidden rounded-lg border border-border bg-card">
                <div className="flex items-start justify-between gap-3 border-b border-border px-4 py-3.5 sm:px-[18px]">
                  <div>
                    <h2 className="text-[15px] font-semibold tracking-tight">Current signing key</h2>
                    <p className="mt-0.5 text-[13px] text-muted-foreground">New tokens are signed with this key.</p>
                  </div>
                  <StatusPill tone="ok">Current</StatusPill>
                </div>
                <div className="space-y-4 p-4 sm:p-[18px]">
                  <div className="space-y-1">
                    <DevLabel>Key ID (kid)</DevLabel>
                    <CopyId value={current.kid} />
                  </div>
                  <StatStrip
                    items={[
                      { label: 'Algorithm', value: <span className="text-lg">{current.algorithm}</span> },
                      { label: 'Created', value: <span className="text-lg">{fmtDate(current.created_at)}</span> },
                      {
                        label: 'Expires',
                        value: <span className="text-lg">{current.expires_at ? fmtDate(current.expires_at) : 'No expiry'}</span>,
                      },
                      left !== null
                        ? { label: 'Days left', value: left, note: 'until this key expires' }
                        : { label: 'Age', value: ageDays, note: 'days in service' },
                    ]}
                  />
                </div>
              </section>
            )}

            <section className="min-w-0 overflow-hidden rounded-lg border border-border bg-card">
              <div className="border-b border-border px-4 py-3.5 sm:px-[18px]">
                <h2 className="text-[15px] font-semibold tracking-tight">All keys</h2>
                <p className="mt-0.5 text-[13px] text-muted-foreground">
                  Retired keys are no longer published and can’t verify tokens.
                </p>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Key ID</TableHead>
                    <TableHead>Algorithm</TableHead>
                    <TableHead>State</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead className="w-24">
                      <span className="sr-only">Actions</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {keys.map((k) => {
                    const s = stateOf(k);
                    const d = daysUntil(k.expires_at);
                    return (
                      <TableRow key={k.kid} className={cn(s === 'retired' && 'opacity-60')}>
                        <TableCell className="min-w-[240px]">
                          <CopyId value={k.kid} />
                        </TableCell>
                        <TableCell>
                          <Tag>{k.algorithm}</Tag>
                        </TableCell>
                        <TableCell>{STATE_PILL[s]}</TableCell>
                        <TableCell className="whitespace-nowrap font-mono text-[12.5px] tabular-nums">
                          {fmtDate(k.created_at)}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {k.expires_at && d !== null ? (
                            <span className="grid">
                              <span className="font-mono text-[12.5px] tabular-nums">{fmtDate(k.expires_at)}</span>
                              <span
                                className={cn(
                                  'text-xs text-muted-foreground',
                                  s !== 'retired' && d <= 30 && 'text-warning'
                                )}
                              >
                                {d > 0 ? `in ${d} day${d === 1 ? '' : 's'}` : `${-d} day${d === -1 ? '' : 's'} ago`}
                              </span>
                            </span>
                          ) : (
                            <span className="text-muted-foreground">{k.is_active ? 'No expiry' : '—'}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {s === 'rotating' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive"
                              onClick={() => setRetireTarget(k)}
                            >
                              Retire
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </section>
          </>
        )}

        <section className="min-w-0 overflow-hidden rounded-lg border border-border bg-card">
          <div className="border-b border-border px-4 py-3.5 sm:px-[18px]">
            <h2 className="text-[15px] font-semibold tracking-tight">JWKS endpoint</h2>
            <p className="mt-0.5 text-[13px] text-muted-foreground">
              Resource servers fetch public keys here to verify tokens. Match on <Mono>kid</Mono>, and
              refetch when you see an unknown one.
            </p>
          </div>
          <div className="space-y-4 p-4 sm:p-[18px]">
            <CopyId value={JWKS_URL} />
            {jwks ? (
              <CodeBlock
                label={`Published keys · ${publishedCount}`}
                code={jwksText}
                maxHeight={320}
              />
            ) : (
              <p className="text-[13px] text-muted-foreground">
                The published key set couldn’t be fetched from this browser.
              </p>
            )}
            <p className="text-xs text-muted-foreground">Modulus values are shortened here; the endpoint returns them in full.</p>
          </div>
        </section>
      </div>

      <ConfirmDialog
        open={confirmRotate}
        onOpenChange={setConfirmRotate}
        title="Rotate the signing key?"
        body={
          <>
            A new RS256 key becomes current and signs every new token.
            {current && (
              <>
                {' '}
                <Mono className="break-all">{current.kid}</Mono> stays published for the overlap window
                so tokens already issued keep verifying.
              </>
            )}{' '}
            Services that cache the JWKS longer than that will reject older tokens.
          </>
        }
        confirmLabel="Rotate key"
        pendingLabel="Rotating…"
        pending={rotateKey.isPending}
        onConfirm={() => rotateKey.mutate()}
      />

      <ConfirmDialog
        open={!!retireTarget}
        onOpenChange={(o) => !o && setRetireTarget(null)}
        danger
        title="Retire this key now?"
        body={
          <>
            <Mono className="break-all">{retireTarget?.kid}</Mono> is removed from the JWKS
            immediately. Any token it signed that hasn’t expired yet will fail verification. Retired
            keys can’t be restored.
          </>
        }
        confirmLabel="Retire key"
        pendingLabel="Retiring…"
        typeToConfirm={retireTarget?.kid}
        pending={retireKey.isPending}
        onConfirm={() => {
          if (retireTarget) retireKey.mutate(retireTarget.kid);
        }}
      />
    </div>
  );
}
